import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../services/api";

const MessengerChatContext = createContext(null);

const MESSENGER_ROLES = new Set(["CUSTOMER", "CAREGIVER", "CARE_MANAGER", "ADMIN", "OPS"]);

const disabledChatApi = {
  connected: false,
  connecting: false,
  messagesByPeer: {},
  loadHistory: async () => [],
  sendText: async () => ({ ok: false }),
  markPeerAsRead: async () => {},
};

export function MessengerChatProvider({ children }) {
  const [chatEnabled, setChatEnabled] = useState(false);
  const [bootstrapError, setBootstrapError] = useState("");
  const [bootstrapping, setBootstrapping] = useState(false);
  const bootstrapGen = useRef(0);
  const role = api.getAuthRoleFromToken?.() || "";
  const canUse = MESSENGER_ROLES.has(role) && Boolean(api.getToken());

  const runBootstrap = useCallback(async () => {
    if (!canUse) {
      setChatEnabled(false);
      setBootstrapError("");
      return;
    }

    const gen = ++bootstrapGen.current;
    setBootstrapping(true);
    setBootstrapError("");

    try {
      const cfgRes = await api.getMessengerConfig();
      const cfg = cfgRes?.config || cfgRes?.data?.config || {};
      if (gen === bootstrapGen.current) {
        setChatEnabled(Boolean(cfg.chatEnabled));
        setBootstrapError(
          cfg.chatEnabled
            ? ""
            : cfg?.setupHints?.[0] || "In-app messenger chat is not available yet."
        );
      }
    } catch (err) {
      if (gen === bootstrapGen.current) {
        setChatEnabled(false);
        setBootstrapError(err?.message || "Unable to load messenger settings.");
      }
    } finally {
      if (gen === bootstrapGen.current) setBootstrapping(false);
    }
  }, [canUse]);

  useEffect(() => {
    runBootstrap();
    return () => {
      bootstrapGen.current += 1;
    };
  }, [canUse, role, runBootstrap]);

  const value = {
    ...disabledChatApi,
    chatCreds: null,
    chatEnabled,
    bootstrapError,
    bootstrapping,
    retryBootstrap: runBootstrap,
  };

  return <MessengerChatContext.Provider value={value}>{children}</MessengerChatContext.Provider>;
}

export function useMessengerChat() {
  return useContext(MessengerChatContext);
}
